import { BookOpen, ChevronLeft, ChevronRight, Lock, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  useMatch,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { kbKeys, useKbMutation, useKbTopics } from '@/hooks/useKb';
import { kb } from '@/lib/api/endpoints';
import type { KbTopic } from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * The knowledge base's own frame: its navigation as a second column beside the
 * app sidebar, content in the third.
 *
 * Topics used to live in the main sidebar and read as a foreign body there —
 * the sidebar answers "where am I in my day", not "where is my FTP guide".
 * One "Znalosti" entry now leads here, and this column owns the rest:
 * search, topics, and the active topic's notes beneath it.
 */
export function KbLayout() {
  const t = useT();
  const navigate = useNavigate();
  // The shell's context (openTask, …) must keep flowing through this nested
  // Outlet, or a task link inside a note would find no way to open a task.
  const shell = useOutletContext();
  const { data: topics } = useKbTopics();

  // On a phone the two columns cannot both fit: the index route shows the
  // list, and choosing a topic or a note swaps to it full width — the ordinary
  // list/detail pattern of a mobile app, rather than two useless slivers.
  const isIndex = useMatch('/kb') !== null;

  const topicMatch = useMatch('/kb/:topicId');
  const urlTopicId =
    topicMatch?.params.topicId === 'notes'
      ? null
      : (topicMatch?.params.topicId ?? null);

  // Which topic's notes are unfolded. Follows the URL, but survives visiting a
  // note (whose URL names no topic) so the column does not collapse mid-read.
  // Adjusted during render with a change guard — the documented pattern for
  // state derived from a prop with memory, and no effect to cascade.
  const [expandedId, setExpandedId] = useState<string | null>(urlTopicId);
  const [seenTopicId, setSeenTopicId] = useState<string | null>(urlTopicId);
  if (urlTopicId !== seenTopicId) {
    setSeenTopicId(urlTopicId);
    if (urlTopicId) setExpandedId(urlTopicId);
  }

  const [query, setQuery] = useState('');
  const { data: found } = useQuery({
    queryKey: kbKeys.search(query.trim()),
    queryFn: () => kb.search(query.trim()),
    enabled: query.trim().length >= 2,
    staleTime: 15_000,
  });

  const [addingTopic, setAddingTopic] = useState(false);
  const [topicDraft, setTopicDraft] = useState('');
  const createTopic = useKbMutation(kb.createTopic, (created) => {
    setTopicDraft('');
    setAddingTopic(false);
    navigate(`/kb/${created.id}`);
  });

  return (
    <div className="flex h-full min-w-0">
      <nav
        aria-label={t('kb.title')}
        className={cn(
          'flex w-64 shrink-0 flex-col border-r border-border bg-surface',
          // Full width while it is the whole screen, hidden once something is
          // open. Above `lg` it is always the column beside the content.
          'max-lg:w-full',
          isIndex ? 'max-lg:flex' : 'max-lg:hidden',
        )}
      >
        <div className="flex items-center gap-2 px-4 pb-2 pt-4">
          <h2 className="text-sm font-semibold text-content">{t('kb.title')}</h2>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => setAddingTopic(true)}
            aria-label={t('kb.newTopic')}
            className="rounded p-1 text-content-subtle transition-surface hover:bg-surface-hover hover:text-content"
          >
            <Plus className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="px-3 pb-2">
          <div className="flex items-center gap-1.5 rounded-md bg-surface-sunken px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-content-subtle" aria-hidden />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('kb.searchNotes')}
              className="w-full bg-transparent text-sm outline-none placeholder:text-content-subtle"
            />
          </div>
        </div>

        <div className="scroll-area flex-1 px-2 pb-3">
          {query.trim().length >= 2 ? (
            <ul className="space-y-0.5">
              {(found ?? []).map((note) => (
                <li key={note.id}>
                  <NoteLink
                    id={note.id}
                    title={note.title}
                    sensitive={note.is_sensitive}
                  />
                </li>
              ))}
              {(found ?? []).length === 0 ? (
                <li className="px-2.5 py-1.5 text-xs text-content-subtle">
                  {t('command.noResults')}
                </li>
              ) : null}
            </ul>
          ) : (
            <>
              {addingTopic ? (
                <input
                  autoFocus
                  value={topicDraft}
                  onChange={(event) => setTopicDraft(event.target.value)}
                  onBlur={() => setAddingTopic(false)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && topicDraft.trim()) {
                      createTopic.mutate(topicDraft.trim());
                    } else if (event.key === 'Escape') {
                      setAddingTopic(false);
                      setTopicDraft('');
                    }
                  }}
                  placeholder={t('kb.newTopic')}
                  className={cn(
                    'mb-1 w-full rounded-md border border-accent bg-surface px-2.5 py-1.5',
                    'text-sm outline-none placeholder:text-content-subtle',
                  )}
                />
              ) : null}
              <ul className="space-y-0.5">
                {(topics ?? []).map((topic) => (
                  <TopicGroup
                    key={topic.id}
                    topic={topic}
                    expanded={expandedId === topic.id}
                    onToggle={() =>
                      setExpandedId((current) =>
                        current === topic.id ? null : topic.id,
                      )
                    }
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      </nav>

      <div
        className={cn(
          'scroll-area min-w-0 flex-1',
          // Nothing selected on a phone means nothing worth showing here; the
          // list already fills the screen.
          isIndex ? 'max-lg:hidden' : 'max-lg:block',
        )}
      >
        {/* The way back to the list, which on a phone is a screen rather than
            a column standing beside this one. */}
        {!isIndex ? (
          <div className="sticky top-0 z-10 border-b border-border bg-surface/95 px-2 py-1.5 backdrop-blur lg:hidden">
            <Link
              to="/kb"
              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-content-muted hover:bg-surface-hover hover:text-content"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              {t('kb.title')}
            </Link>
          </div>
        ) : null}
        <Outlet context={shell} />
      </div>
    </div>
  );
}

/** One topic: the row navigates, the chevron unfolds its notes underneath. */
function TopicGroup({
  topic,
  expanded,
  onToggle,
}: {
  topic: KbTopic;
  expanded: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  const { data: notes } = useQuery({
    queryKey: kbKeys.notes(topic.id),
    queryFn: () => kb.notes(topic.id),
    enabled: expanded,
  });

  return (
    <li>
      <div className="flex items-center">
        <button
          type="button"
          onClick={onToggle}
          aria-label={topic.name}
          aria-expanded={expanded}
          className="rounded p-1 text-content-subtle hover:text-content"
        >
          <ChevronRight
            className={cn(
              'h-3.5 w-3.5 transition-transform',
              expanded ? 'rotate-90' : '',
            )}
            aria-hidden
          />
        </button>
        <NavLink
          to={`/kb/${topic.id}`}
          className={({ isActive }) =>
            cn(
              'flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm',
              'transition-surface',
              isActive
                ? 'bg-surface-hover font-medium text-content'
                : 'text-content-muted hover:bg-surface-hover hover:text-content',
            )
          }
        >
          <BookOpen className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate">
            {topic.name}
            {topic.owner ? (
              <span className="ml-1 text-[10px] text-content-subtle">
                · {topic.owner.username}
              </span>
            ) : null}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-content-subtle">
            {topic.note_count + topic.secret_count || ''}
          </span>
        </NavLink>
      </div>

      {expanded ? (
        <ul className="ml-[1.4rem] space-y-0.5 border-l border-border pl-1.5">
          {(notes ?? []).map((note) => (
            <li key={note.id}>
              <NoteLink id={note.id} title={note.title} sensitive={note.is_sensitive} />
            </li>
          ))}
          {(notes ?? []).length === 0 ? (
            <li className="px-2 py-1 text-xs text-content-subtle">
              {t('kb.empty.title')}
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}

function NoteLink({
  id,
  title,
  sensitive,
}: {
  id: string;
  title: string;
  sensitive: boolean;
}) {
  return (
    <NavLink
      to={`/kb/notes/${id}`}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] transition-surface',
          isActive
            ? 'bg-surface-hover font-medium text-content'
            : 'text-content-muted hover:bg-surface-hover hover:text-content',
        )
      }
    >
      {sensitive ? (
        <Lock className="h-3 w-3 shrink-0 text-amber-600" aria-hidden />
      ) : null}
      <span className="min-w-0 truncate">{title}</span>
    </NavLink>
  );
}

/** /kb with nothing selected. */
export function KbIndexRoute() {
  const t = useT();
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex flex-col items-center gap-2 text-center">
        <BookOpen className="h-6 w-6 text-content-subtle" aria-hidden />
        <p className="text-sm text-content-subtle">{t('kb.pickTopic')}</p>
      </div>
    </div>
  );
}
